/**
 * Thermal Printer — ESC/POS via Web Bluetooth API
 *
 * Connects directly to Bluetooth thermal printers (like MPT-II)
 * and sends raw ESC/POS commands. No system printer setup needed.
 * No print dialog, no preview.
 */

const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

const CMD = {
  INIT: [ESC, 0x40],
  ALIGN_CENTER: [ESC, 0x61, 0x01],
  ALIGN_LEFT: [ESC, 0x61, 0x00],
  BOLD_ON: [ESC, 0x45, 0x01],
  BOLD_OFF: [ESC, 0x45, 0x00],
  DOUBLE_SIZE: [ESC, 0x21, 0x30],
  DOUBLE_HEIGHT: [ESC, 0x21, 0x10],  // double height only, normal width
  NORMAL_SIZE: [ESC, 0x21, 0x00],
  FEED_AND_CUT: [GS, 0x56, 0x41, 0x03],
  FEED_LINES: (n) => [ESC, 0x64, n],
};

const encoder = new TextEncoder();

const LOGO_URL = "https://d2nahbmqd5vvug.cloudfront.net/nxtmeal/nxtMealLogo.png";
let _cachedLogoRaster = null;

/**
 * Known Bluetooth printer service UUIDs.
 * Different printer brands use different UUIDs.
 * We try all common ones during discovery.
 */
const KNOWN_SERVICE_UUIDS = [
  "000018f0-0000-1000-8000-00805f9b34fb", // Common thermal printers
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2", // Generic BLE printers
  "49535343-fe7d-4ae5-8fa9-9fafd205e455", // Microchip/ISSC (many Chinese printers)
];

const KNOWN_WRITE_CHAR_UUIDS = [
  "00002af1-0000-1000-8000-00805f9b34fb", // Common write characteristic
  "bef8d6c9-9c21-4c9e-b632-bd58c1009f9f", // Generic BLE printers
  "49535343-8841-43f4-a8d4-ecbe34729bb3", // Microchip/ISSC write
];

class ThermalPrinter {
  constructor({ lineWidth = 32 } = {}) {
    this.device = null;
    this.server = null;
    this.writeCharacteristic = null;
    this.lineWidth = lineWidth; // 32 for 58mm, 48 for 80mm
    this._connected = false;
  }

  get isConnected() {
    return this._connected && this.device?.gatt?.connected && this.writeCharacteristic != null;
  }

  /**
   * Connect to a Bluetooth thermal printer.
   * Must be called from a user gesture (click).
   */
  async connect() {
    if (!("bluetooth" in navigator)) {
      throw new Error("Web Bluetooth not supported. Use Chrome 56+ on desktop.");
    }

    // Request any Bluetooth device — we accept all services
    // since printer UUIDs vary widely
    console.log("[BTPrinter] Requesting device...");
    this.device = await navigator.bluetooth.requestDevice({
      // Accept all devices, filter by name prefix if possible
      filters: [
        { namePrefix: "MPT" },
        { namePrefix: "Printer" },
        { namePrefix: "BlueTooth" },
        { namePrefix: "BT" },
      ],
      optionalServices: KNOWN_SERVICE_UUIDS,
    });

    console.log("[BTPrinter] Selected device:", this.device.name);

    // Listen for disconnect
    this.device.addEventListener("gattserverdisconnected", () => {
      console.log("[BTPrinter] Disconnected");
      this._connected = false;
      this.writeCharacteristic = null;
    });

    await this._connectGatt();
  }

  /**
   * Fallback connect that accepts ANY Bluetooth device.
   */
  async connectAny() {
    if (!("bluetooth" in navigator)) {
      throw new Error("Web Bluetooth not supported. Use Chrome 56+ on desktop.");
    }

    console.log("[BTPrinter] Requesting ANY device...");
    this.device = await navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: KNOWN_SERVICE_UUIDS,
    });

    console.log("[BTPrinter] Selected device:", this.device.name);

    this.device.addEventListener("gattserverdisconnected", () => {
      console.log("[BTPrinter] Disconnected");
      this._connected = false;
      this.writeCharacteristic = null;
    });

    await this._connectGatt();
  }

  async _connectGatt() {
    console.log("[BTPrinter] Connecting to GATT server...");
    this.server = await this.device.gatt.connect();

    // Discover services and find writable characteristic
    console.log("[BTPrinter] Discovering services...");
    const services = await this.server.getPrimaryServices();
    console.log("[BTPrinter] Found", services.length, "services:",
      services.map((s) => s.uuid));

    for (const service of services) {
      try {
        const chars = await service.getCharacteristics();
        console.log("[BTPrinter] Service", service.uuid, "has", chars.length, "characteristics:",
          chars.map((c) => ({ uuid: c.uuid, props: c.properties })));

        for (const char of chars) {
          // Look for a writable characteristic
          if (
            char.properties.write ||
            char.properties.writeWithoutResponse
          ) {
            // Prefer known write characteristic UUIDs
            if (
              KNOWN_WRITE_CHAR_UUIDS.includes(char.uuid) ||
              !this.writeCharacteristic
            ) {
              this.writeCharacteristic = char;
              console.log("[BTPrinter] Using write characteristic:", char.uuid,
                "write:", char.properties.write,
                "writeWithoutResponse:", char.properties.writeWithoutResponse);
            }
          }
        }
      } catch (err) {
        console.log("[BTPrinter] Skipping service", service.uuid, ":", err.message);
      }
    }

    if (!this.writeCharacteristic) {
      throw new Error("No writable characteristic found on this printer.");
    }

    this._connected = true;
    console.log("[BTPrinter] Ready!");

    // Preload logo in background
    this.preloadLogo();
  }

  async disconnect() {
    try {
      if (this.device?.gatt?.connected) {
        this.device.gatt.disconnect();
      }
    } catch (e) {
      // ignore
    }
    this._connected = false;
    this.writeCharacteristic = null;
  }

  /**
   * Write data in chunks (BLE has a ~512 byte MTU limit, safe at 100).
   */
  async _write(data) {
    if (!this.isConnected) throw new Error("Printer not connected");

    const bytes = new Uint8Array(data);
    const CHUNK = 100; // Safe BLE chunk size

    for (let i = 0; i < bytes.length; i += CHUNK) {
      const chunk = bytes.slice(i, i + CHUNK);
      if (this.writeCharacteristic.properties.writeWithoutResponse) {
        await this.writeCharacteristic.writeValueWithoutResponse(chunk);
      } else {
        await this.writeCharacteristic.writeValueWithResponse
          ? await this.writeCharacteristic.writeValueWithResponse(chunk)
          : await this.writeCharacteristic.writeValue(chunk);
      }
    }
  }

  /** Simple test print */
  async testPrint() {
    if (!this.isConnected) throw new Error("Printer not connected");
    const testBytes = [
      ...CMD.INIT,
      ...CMD.ALIGN_CENTER,
      ...CMD.BOLD_ON,
      ...encoder.encode("=== TEST PRINT ==="),
      LF, LF,
      ...encoder.encode("Printer is working!"),
      LF, LF,
      ...CMD.BOLD_OFF,
      ...encoder.encode(new Date().toLocaleString()),
      LF, LF, LF,
    ];
    console.log("[BTPrinter] Test print:", testBytes.length, "bytes");
    await this._write(testBytes);
    console.log("[BTPrinter] Test print sent!");
  }

  /** Preload logo so first print isn't delayed */
  async preloadLogo() {
    if (_cachedLogoRaster) return;
    try {
      await this._getLogoRaster(120);
    } catch { /* ignore */ }
  }

  /**
   * Fetch logo PNG → monochrome raster (GS v 0). Cached.
   * maxWidthPx kept tiny (80) so data is ~400 bytes.
   */
  async _getLogoRaster(maxWidthPx) {
    if (_cachedLogoRaster) return _cachedLogoRaster;

    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = LOGO_URL;
    });

    let w = img.width;
    let h = img.height;
    if (w > maxWidthPx) {
      h = Math.round((h * maxWidthPx) / w);
      w = maxWidthPx;
    }
    w = Math.floor(w / 8) * 8; // must be multiple of 8
    if (w === 0) return null;

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);

    const pixels = ctx.getImageData(0, 0, w, h).data;
    const bytesPerRow = w / 8;
    const raster = new Uint8Array(bytesPerRow * h);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const i = (y * w + x) * 4;
        const gray = pixels[i] * 0.299 + pixels[i + 1] * 0.587 + pixels[i + 2] * 0.114;
        if (gray < 128) {
          raster[y * bytesPerRow + Math.floor(x / 8)] |= (0x80 >> (x % 8));
        }
      }
    }

    const xL = bytesPerRow & 0xff;
    const xH = (bytesPerRow >> 8) & 0xff;
    const yL = h & 0xff;
    const yH = (h >> 8) & 0xff;
    _cachedLogoRaster = new Uint8Array([GS, 0x76, 0x30, 0x00, xL, xH, yL, yH, ...raster]);
    console.log("[BTPrinter] Logo cached:", _cachedLogoRaster.length, "bytes");
    return _cachedLogoRaster;
  }

  /** Print full order receipt */
  async printOrder(order) {
    if (!this.isConnected) throw new Error("Printer not connected");

    // Send logo separately first (if available)
    let logoBytes = _cachedLogoRaster;
    if (!logoBytes) {
      try { logoBytes = await this._getLogoRaster(120); } catch { /* skip */ }
    }
    if (logoBytes) {
      await this._write([...CMD.INIT]);
      await this._write([...CMD.ALIGN_CENTER]);
      await this._write(logoBytes);
    }

    // Then send the text receipt
    const bytes = this._buildReceipt(order, !logoBytes);
    console.log("[BTPrinter] Printing order", order.orderNumber || order.orderID, ":", bytes.length, "bytes");
    await this._write(bytes);
    console.log("[BTPrinter] Order printed!");
  }

  _buildReceipt(order, showTextBrand = true) {
    const w = this.lineWidth;
    const buf = [];
    const push = (...args) =>
      args.forEach((a) =>
        buf.push(...(Array.isArray(a) ? a : encoder.encode(String(a))))
      );
    const line = (text) => push(text, [LF]);
    const dashes = () => line("-".repeat(w));
    const emptyLine = () => push([LF]);

    // --- Brand (text fallback only when logo failed) ---
    if (showTextBrand) {
      push(CMD.INIT);
      push(CMD.ALIGN_CENTER, CMD.BOLD_ON);
      line("NXTMEAL");
      push(CMD.BOLD_OFF);
      emptyLine();
    }

    // --- Order Number ---
    push(CMD.ALIGN_CENTER, CMD.BOLD_ON);
    line("ORDER #" + (order.orderNumber || ""));
    push(CMD.BOLD_OFF);
    emptyLine();

    // --- Client & Location ---
    if (order.clientName) {
      push(CMD.ALIGN_CENTER, CMD.BOLD_ON);
      line(order.clientName);
      push(CMD.BOLD_OFF);
    }
    const loc = [order.locationName, order.subLocationName].filter(Boolean).join(", ");
    if (loc) {
      push(CMD.ALIGN_CENTER);
      line(loc);
    }
    dashes();

    // --- Customer Details ---
    push(CMD.ALIGN_LEFT);
    if (order.craverName) {
      line(this._labelValue("Name", order.craverName, w));
    }
    if (order.deliveryDate) {
      line(this._labelValue("Date", order.deliveryDate, w));
    }
    const timeStr = order.createdTime
      || (order.createdAtEpoch ? new Date(order.createdAtEpoch).toLocaleTimeString() : "");
    if (timeStr) {
      line(this._labelValue("Time", timeStr, w));
    }
    if (order.paymentStatus) {
      line(this._labelValue("Payment", order.paymentStatus, w));
    }
    if (order.scheduledSlot) {
      push(CMD.BOLD_ON);
      line(this._labelValue("Scheduled", order.scheduledSlot, w));
      push(CMD.BOLD_OFF);
    }
    dashes();

    // --- Items Header ---
    push(CMD.BOLD_ON);
    line(this._padRow("Item", "Qty", w));
    push(CMD.BOLD_OFF);
    dashes();

    // --- Items ---
    (order.orderedItems || []).forEach((item) => {
      const name = item.itemName || item.menuName || "";
      const qty = String(item.quantity || 0);
      const maxName = w - 6;
      if (name.length > maxName) {
        line(name);
        line(this._padRow("", qty, w));
      } else {
        line(this._padRow(name, qty, w));
      }
    });
    dashes();

    // --- Total ---
    push(CMD.BOLD_ON, CMD.ALIGN_LEFT);
    line(this._padRow("Total", (order.payableAmount || order.totalAmount || 0) + "/-", w));
    push(CMD.BOLD_OFF);
    emptyLine();

    // --- Footer ---
    push(CMD.ALIGN_CENTER, CMD.BOLD_ON);
    line("*****Enjoy your meal*****");
    push(CMD.BOLD_OFF);
    emptyLine();
    push(CMD.FEED_LINES(9));

    return buf;
  }

  _padRow(name, qty, width) {
    const qStr = String(qty);
    const nw = width - qStr.length;
    return (name.length > nw ? name.substring(0, nw) : name.padEnd(nw)) + qStr;
  }

  _labelValue(label, value, width) {
    const prefix = label + " - ";
    const maxVal = width - prefix.length;
    const val = String(value);
    return prefix + (val.length > maxVal ? val.substring(0, maxVal) : val);
  }
}

export default ThermalPrinter;
